import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
export interface WorldClock {
  city: string;
  time: string; // HH:MM
  utcOffset: number;
  isReference?: boolean;
}
export interface WorldClockPanelProps {
  clocks: WorldClock[];
  label?: string;
  mode?: 'stimulus' | 'support';
}
export function WorldClockPanel({
  clocks,
  label,
  mode = 'support'
}: WorldClockPanelProps) {
  const isStimulus = mode === 'stimulus';
  // Sort clocks so reference is first, or keep original order if no reference
  const sortedClocks = [...clocks].sort((a, b) => {
    if (a.isReference && !b.isReference) return -1;
    if (!a.isReference && b.isReference) return 1;
    return 0;
  });
  const formatOffset = (offset: number) => {
    return offset >= 0 ? `UTC +${offset}` : `UTC ${offset}`;
  };
  const calculateDifference = (clock1: WorldClock, clock2: WorldClock) => {
    const diff = clock2.utcOffset - clock1.utcOffset;
    return diff > 0 ? `+${diff} uur` : `${diff} uur`;
  };
  return <div className={`relative overflow-hidden ${isStimulus ? 'bg-sky-50 rounded-xl px-4 pt-4 pb-6 border border-sky-200' : 'bg-gradient-to-br from-sky-50 to-blue-50 p-6 rounded-2xl border-2 border-sky-200 shadow-sm'}`} aria-label="Wereldklokken paneel" data-id="element-14311" data-mp="3otwkxux7ewpq">
      {/* Subtle Globe Background */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] flex items-center justify-center" data-id="element-14312" data-mp="3a5ii3jzsf896">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className="w-full h-full max-w-md" data-id="element-14313" data-mp="bnmonpbkxnj5">
          <circle cx="12" cy="12" r="10" data-id="element-14314" data-mp="1ox4tr04ht09" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" data-id="element-14315" data-mp="1nvrffvmwufd" />
          <path d="M2 12h20" data-id="element-14316" data-mp="3tsg981z9skl9" />
        </svg>
      </div>

      <div className="relative z-10" data-id="element-14317" data-mp="2mfxnu4y6ookl">
        {label && <p className={`text-sm font-semibold text-sky-800 mb-6 text-center ${!isStimulus ? 'text-base' : ''}`} data-id="element-14318" data-mp="2mdbhu9qxyh4h">
            {label}
          </p>}

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8" data-id="element-14319" data-mp="2jvribstx8q1x">
          {sortedClocks.map((clock, index) => <Fragment key={clock.city} data-id="element-14320" data-mp="29x288m89o9w8" data-mpi={index}>
              <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} className={`flex flex-col items-center p-4 rounded-xl min-w-[120px] ${clock.isReference ? 'bg-white border-2 border-sky-400 shadow-md' : 'bg-white/80 border border-sky-200 shadow-sm'}`} data-id="element-14321" data-mp="29w0pdvl0ej3y">
                <span className="text-sm font-medium text-slate-500 mb-1" data-id="element-14322" data-mp="1w32ltlyexkpb">
                  {clock.city}
                </span>
                <span className="text-2xl font-bold text-slate-800 font-mono tracking-wider mb-2" data-id="element-14323" data-mp="1m7r6b1f6k9gi">
                  {clock.time}
                </span>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${clock.isReference ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-14324" data-mp="13j7882udtx01">
                  {formatOffset(clock.utcOffset)}
                </span>
              </motion.div>
              {/* Arrow between clocks */}
              {index < sortedClocks.length - 1 && <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: index * 0.1 + 0.2
          }} className="flex flex-col items-center text-sky-400" data-id="element-14325" data-mp="1mfyo02apttzc">
                  {!isStimulus && <span className="text-xs font-bold text-sky-600 mb-1 whitespace-nowrap" data-id="element-14326" data-mp="20aiwvql2t136">
                      {calculateDifference(clock, sortedClocks[index + 1])}
                    </span>}
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="hidden sm:block" data-id="element-14327" data-mp="2obcu7t33ajlg">
                    <line x1="5" y1="12" x2="19" y2="12" data-id="element-14328" data-mp="25etiqx4tcjlq"></line>
                    <polyline points="12 5 19 12 12 19" data-id="element-14329" data-mp="2aclk0hrcli2l"></polyline>
                  </svg>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="block sm:hidden" data-id="element-14330" data-mp="2fby630aeal44">
                    <line x1="12" y1="5" x2="12" y2="19" data-id="element-14331" data-mp="1a4y8pxomu2wp"></line>
                    <polyline points="19 12 12 19 5 12" data-id="element-14332" data-mp="1a5q6nf5pccq5"></polyline>
                  </svg>
                </motion.div>}
            </Fragment>)}
        </div>

        {!isStimulus && sortedClocks.length >= 2 && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="mt-6 bg-white/80 rounded-xl p-4 border border-sky-100 text-sm text-slate-700" data-id="element-14333" data-mp="wdjndk795pmh">
            <div className="flex items-start gap-2" data-id="element-14334" data-mp="hhc7gw2vnad0">
              <span className="text-lg" data-id="element-14335" data-mp="hinc0y45cm56">🌍</span>
              <div data-id="element-14336" data-mp="2gsbyvv2fzw96">
                <p className="font-semibold text-sky-800 mb-1" data-id="element-14337" data-mp="2gsv6ahugv28y">
                  Tijdverschil berekenen
                </p>
                <p data-id="element-14338" data-mp="2bsh77pauf2n2">
                  Naar het <strong data-id="element-14339" data-mp="a4md2quh8ua8">oosten</strong> (later op de dag) = uren
                  erbij optellen (+).
                  <br data-id="element-14340" data-mp="ewcq54kstw5c" />
                  Naar het <strong data-id="element-14341" data-mp="exfjrk9iacoy">westen</strong> (vroeger op de dag) = uren
                  eraf halen (−).
                </p>
              </div>
            </div>
          </motion.div>}
      </div>
    </div>;
}